import { createSelector } from 'reselect';
import { EmptyObject } from 'resources/EmptyDefaults';
import { getEntities } from 'resources/EntityData';
import { FeaturesApi } from 'resources/Organization/Features/FeaturesApi';
import { fetchOrganizationFeatures } from 'resources/Organization/Features/FeaturesData';
import ProApi from 'resources/Pro/ProApi';
import { fetchPro } from 'resources/Pro/ProData';
import { ENTITY_VOICE_PLANS, voicePlanSchema } from 'schema/VoicePlanSchema';
import { isPresent } from 'utils/isPresent';

import RestAPI from 'components/Common/restApi';
import RedundancyApi from 'components/Voice/Redundancy/RedundancyApi';

import { Product } from '../../PayWall/ProductPayWall/types';

export const PACKAGE_SELECTION_PROPERTIES = { button_label: 'package selection' };
export const PACKAGE_SELECTION_VOIP_BUTTON_NAME = 'voip + call tracking';
export const PACKAGE_SELECTION_CALL_TRACKING_BUTTON_NAME = 'call tracking only';
export const PACKAGE_SELECTION_SCHEDULE_A_DEMO_BUTTON_NAME = 'schedule a demo';
export const PAYMENT_SUMMARY_BUTTON_PROPERTIES = { button_label: 'payment summary' };
export const PAYMENT_SUMMARY_CANCEL_BUTTON_NAME = 'cancel';
export const PAYMENT_SUMMARY_CHANGE_PAYMENT_BUTTON_NAME = 'change payment method';
export const PAYMENT_SUMMARY_CHANGE_ADDRESS_BUTTON_NAME = 'change company address';
export const PAYMENT_SUMMARY_CONFIRM_PAYMENT_BUTTON_NAME = 'confirm payment';
export const PAYMENT_SUMMARY_BACK_ARROW_BUTTON_NAME = 'back arrow';
export const PAYMENT_SUMMARY_SUCCESS_BUTTON_PROPERTIES = { button_label: 'payment confirmation' };
export const PAYMENT_SUMMARY_SUCCESS_BUTTON_NAME = 'get started';
export const VOICE_DEVICE_MAP = {
  web: 'web phone',
  mobile: 'mobile app',
};
export type VoiceBillingPlan = {
  descriptions: string[];
  id: number;
  label: string;
  price: number;
  product_descritpion: string;
  rates: {
    id: number;
    rate_type: 'flat_rate' | 'overage_unit' | 'quantitative_flat_rate';
    item_type: string;
    quantity: number;
    unit_price: number;
    free_pool: number;
  }[];
};

type VoicePlan = {
  callTrackingCount: number;
  id: number;
  planMinutes: number;
  planPrice: number;
  planTier: string;
  planType: string;
};

type VoicePlanTier = {
  Low: Partial<VoicePlan>;
  Mid: Partial<VoicePlan>;
  High: Partial<VoicePlan>;
};

type FormattedVoicePlans = {
  callTrackingOnlyPlans: VoicePlanTier;
  voipPlans: VoicePlanTier;
};

export const fetchOrganizationFeaturesGracefully = {
  ...fetchOrganizationFeatures,
  fetchingApiFunc: () =>
    FeaturesApi.index().catch(() => {
      return { data: { data: [{ server_failure: true }] } };
    }),
};

export const fetchProIdentity = {
  ...fetchPro,
  fetchingApiFunc: () =>
    ProApi.fetchPro().catch(() => {
      return RedundancyApi.fetchPro();
    }),
};

const formatVoicePlans = (plans: VoiceBillingPlan[]): FormattedVoicePlans => {
  if (isPresent(plans)) {
    const reducedPlans = plans.reduce<FormattedVoicePlans>(
      (acc, plan) => {
        const planTier = () => {
          if (plan.label.includes('Low')) return 'Low';
          if (plan.label.includes('High')) return 'High';
          return 'Mid';
        };
        if (plan.label.toLowerCase().includes('voip')) {
          acc.voipPlans[planTier()] = {
            callTrackingCount: plan.rates.find((rate) => rate?.item_type === 'voip_line')?.free_pool,
            id: plan.id,
            planMinutes: plan.rates.find((rate) => rate?.item_type === 'voip_overage_minutes')?.free_pool,
            planPrice: plan.price,
            planTier: planTier(),
            planType: plan.label.includes('VoIP') ? 'voip' : 'call tracking',
          };
        } else {
          acc.callTrackingOnlyPlans[planTier()] = {
            callTrackingCount: plan.rates.find((rate) => rate?.item_type === 'call_tracking_line')?.free_pool,
            id: plan.id,
            planMinutes: plan.rates.find((rate) => rate?.item_type === 'call_tracking_overage_minutes')?.free_pool,
            planPrice: plan.price,
            planTier: planTier(),
            planType: plan.label.includes('VoIP') ? 'voip' : 'call tracking',
          };
        }
        return acc;
      },
      {
        voipPlans: { Low: {}, Mid: {}, High: {} },
        callTrackingOnlyPlans: { Low: {}, Mid: {}, High: {} },
      } as FormattedVoicePlans
    );
    return reducedPlans;
  }
  return {
    voipPlans: { Low: {}, Mid: {}, High: {} },
    callTrackingOnlyPlans: { Low: {}, Mid: {}, High: {} },
  };
};

const getVoicePlans = createSelector(getEntities, (entities) => entities[ENTITY_VOICE_PLANS] || EmptyObject);

export const fetchVoicePlans = {
  fetchingApiFunc: () =>
    RestAPI.getWithFormData('/pro/paywall/subscriptions/tiers', { product_identifier: Product.Voice }),
  schema: voicePlanSchema,
  selector: getVoicePlans,
  paramsFromProps: () => [],
};

export const getFormatVoicePlans = createSelector(getVoicePlans, (data) => formatVoicePlans(data?.data?.plans));
